import React from "react";
import { Pagination } from "react-bootstrap";

interface PaginationControlsProps {
  currentPage: number;
  totalPages: number;
  totalCount: number;
  pageSize: number;
  onPageChange: (page: number) => void;
}

/**
 * Pagination control component (bottom of table)
 */
const PaginationControls = ({
  currentPage,
  totalPages,
  totalCount,
  pageSize,
  onPageChange,
}: PaginationControlsProps) => {
  // Don't show pagination if everything fits on one page
  if (totalPages <= 1 || totalCount === 0) {
    return null;
  }

  //calculate the current page index
  const startRecord = (currentPage - 1) * pageSize + 1;
  const endRecord = Math.min(currentPage * pageSize, totalCount);

  /**
   * Generate page numbers with ellipsis
   */
  const generatePageNumbers = (): (number | string)[] => {
    const delta = 2;
    const pages: (number | string)[] = [];

    pages.push(1);

    //calculate display range and add into pages
    const rangeStart = Math.max(2, currentPage - delta);
    const rangeEnd = Math.min(totalPages - 1, currentPage + delta);

    //add ellipsis if there is gap between first page and rangeStart
    if (rangeStart > 2) {
      pages.push("...");
    }

    //add the in-between pages
    for (let i = rangeStart; i <= rangeEnd; i++) {
      pages.push(i);
    }

    //add ellipsis if there is gap between rangeEnd and last page
    if (rangeEnd < totalPages - 1) {
      pages.push("...");
    }

    //show the last page
    if (totalPages > 1) {
      pages.push(totalPages);
    }

    return pages;
  };

  return (
    <div className="d-flex justify-content-between align-items-center mt-3">
      {/* Left side: Current range info */}
      <div className="text-muted">
        Zeige <strong>{startRecord}</strong> bis <strong>{endRecord}</strong>{" "}
        von <strong>{totalCount}</strong>
      </div>

      {/* Right side: Pagination controls */}
      <Pagination className="mb-0">
        {/**the first page */}
        <Pagination.First
          disabled={currentPage === 1}
          onClick={() => onPageChange(1)}
          title="Erste Seite"
        />

        {/**previous page */}
        <Pagination.Prev
          disabled={currentPage === 1}
          onClick={() => onPageChange(currentPage - 1)}
          title="Vorherige Seite"
        />

        {/**page number*/}
        {generatePageNumbers().map((page, index) => {
          if (page === "...") {
            return <Pagination.Ellipsis key={`ellipsis-${index}`} disabled />;
          }
          return (
            <Pagination.Item
              key={page}
              active={page === currentPage}
              onClick={() => onPageChange(page as number)}
            >
              {page}
            </Pagination.Item>
          );
        })}

        {/* Next page */}
        <Pagination.Next
          disabled={currentPage === totalPages}
          onClick={() => onPageChange(currentPage + 1)}
          title="Nächste Seite"
        />

        {/* Last page */}
        <Pagination.Last
          disabled={currentPage === totalPages}
          onClick={() => onPageChange(totalPages)}
          title="Letzte Seite"
        />
      </Pagination>
    </div>
  );
};

export default PaginationControls;
